import Vue from 'vue';
import VueCookies from 'vue-cookies';
Vue.use(VueCookies);
function addSpacesBeforeCapitals(str) {
    return str.replace(/([A-Z])/g, ' $1').trim();
}

const DEFAULT_DISTANCE = 30;

// Kyruus applies these left to right; the first term decides the order.
export const DEFAULT_SORT = 'relevance,networks,availability_soonest,distance';

// The dropdown renders this and incoming URLs are validated against it - one list, so
// the select can never disagree with the query.
export const SORT_OPTIONS = [
    { name: 'Best Match', value: DEFAULT_SORT },
    { name: 'Next Available Appointment', value: 'availability_soonest,networks,distance' }
];

const isSupportedSort = (sort) => SORT_OPTIONS.some(option => option.value === sort);

export default {
    state: {
        HasUrlParams: false,
        Keyword: null,
        Specialties: null,
        ProviderName: null,
        Location: null,
        Distance: DEFAULT_DISTANCE,
        // Read-only mirror of the header's global "Set My Location" preference
        GlobalLocation: null,
        Insurances: null,
        showInsurancesBlock: false,
        showTravelTime: false,
        AcceptingNewPatients: null,
        OffersOnlineScheduling: null,
        OffersVirtualVisits: null,
        NextAvailability: null,
        PreferredDays: null,
        PreferredTimes: [],
        Genders: [],
        Languages: [],
        Latitude: null,
        Longitude: null,
        LocationName: null,
        Page: 1,
        PageSize: 10,
        Sort: DEFAULT_SORT,
        Seed: 'christus',
        fetchingAppointments: false,
        Appointments: [],
        ProvidersByLocation: [],
        ClusterLabel: null,
        locationsLoading: false,
        ActiveFilters: {},
        resultsLoading: false,
        results: {
            providers: []
        },
        resultsLabel: '',
        filterRemoved: false,
        distanceRemoved: false,
        clearAll: false,
        mobileFilterActive: false,
        firstLoad: true
    },
    mutations: {
        HasUrlParams: (state, HasUrlParams) => { state.HasUrlParams = HasUrlParams },
        Keyword: (state, Keyword) => { state.Keyword = Keyword },
        KeywordCategory: (state, KeywordCategory) => { state.KeywordCategory = KeywordCategory },
        Specialties: (state, Specialties) => { state.Specialties = Specialties },
        ProviderName: (state, ProviderName) => { state.ProviderName = ProviderName },
        Location: (state, Location) => { state.Location = Location },
        Distance: (state, Distance) => { state.Distance = Distance },
        Insurances: (state, Insurances) => { state.Insurances = Insurances },
        showInsurancesBlock: (state, showInsurancesBlock) => { state.showInsurancesBlock = showInsurancesBlock },
        showTravelTime: (state, showTravelTime) => { state.showTravelTime = showTravelTime },
        AcceptingNewPatients: (state, AcceptingNewPatients) => { state.AcceptingNewPatients = AcceptingNewPatients },
        OffersOnlineScheduling: (state, OffersOnlineScheduling) => { state.OffersOnlineScheduling = OffersOnlineScheduling },
        OffersVirtualVisits: (state, OffersVirtualVisits) => { state.OffersVirtualVisits = OffersVirtualVisits },
        NextAvailability: (state, NextAvailability) => { state.NextAvailability = NextAvailability },
        PreferredDays: (state, PreferredDays) => { state.PreferredDays = PreferredDays },
        PreferredTimes: (state, PreferredTimes) => { state.PreferredTimes = PreferredTimes },
        Genders: (state, Genders) => { state.Genders = Genders },
        Languages: (state, Languages) => { state.Languages = Languages },
        Latitude: (state, Latitude) => { state.Latitude = Latitude },
        Longitude: (state, Longitude) => { state.Longitude = Longitude },
        LocationName: (state, LocationName) => { state.LocationName = LocationName },
        GlobalLocation: (state, GlobalLocation) => { state.GlobalLocation = GlobalLocation },
        Page: (state, Page) => { state.Page = Page },
        PageSize: (state, PageSize) => { state.PageSize = PageSize },
        Sort: (state, Sort) => { state.Sort = Sort },
        Seed: (state, Seed) => { state.Seed = Seed },
        fetchingAppointments: (state, fetchingAppointments) => { state.fetchingAppointments = fetchingAppointments },
        Appointments: (state, Appointments) => { state.Appointments = Appointments },
        ProvidersByLocation: (state, ProvidersByLocation) => { state.ProvidersByLocation = ProvidersByLocation },
        ClusterLabel: (state, ClusterLabel) => { state.ClusterLabel = ClusterLabel },
        addOrUpdateFilter(state, { key, value }) { Vue.set(state.ActiveFilters, key, value); },
        removeFilter(state, key) {
            Vue.delete(state.ActiveFilters, key);
            
            // Wipe actual state property
            if (state.hasOwnProperty(key)) {
                state[key] = null;
            }

            // A null radius reaches the API as "no radius at all", which is never what
            // the sidebar shows - one distance button is always active. Removing the
            // chip means "back to the default radius", not "search every state".
            if (key === 'Distance') {
                state.Distance = DEFAULT_DISTANCE;
            }

            // Wipes ALL interconnected text searches regardless of which X was clicked
            if (key === 'Keyword' || key === 'Specialties' || key === 'ProviderName') {
                state.Keyword = null;
                state.KeywordCategory = null;
                state.Specialties = null;
                state.ProviderName = null;

                Vue.delete(state.ActiveFilters, 'Keyword');
                Vue.delete(state.ActiveFilters, 'Specialties');
                Vue.delete(state.ActiveFilters, 'ProviderName');
            }

            // Tell components to sync their local inputs
            state.filterRemoved = true; 
        },

        removeArrayElement(state, { key, index }) {
            if (Array.isArray(state.ActiveFilters[key])) {
                state.ActiveFilters[key].splice(index, 1);
            }
        },
        removeAllFilters(state) {
            for (const key in state.ActiveFilters) {
                if (state.ActiveFilters.hasOwnProperty(key)) {
                    Vue.delete(state.ActiveFilters, key);
                }
            }
            state.Keyword = null;
            state.KeywordCategory = null;
            state.Specialties = null;
            state.ProviderName = null;
            state.filterRemoved = true;
        },
        resultsLoading: (state, resultsLoading) => { state.resultsLoading = resultsLoading },
        locationsLoading: (state, locationsLoading) => { state.locationsLoading = locationsLoading },
        results: (state, results) => { state.results = results },
        resultsLabel: (state, resultsLabel) => { state.resultsLabel = resultsLabel },
        filterRemoved: (state, filterRemoved) => { state.filterRemoved = filterRemoved },
        distanceRemoved: (state, distanceRemoved) => { state.distanceRemoved = distanceRemoved },
        keywordRemoved: (state, keywordRemoved) => { state.keywordRemoved = keywordRemoved },
        clearAll: (state, clearAll) => { state.clearAll = clearAll },
        mobileFilterActive: (state, mobileFilterActive) => { state.mobileFilterActive = mobileFilterActive },
        firstLoad: (state, firstLoad) => { state.firstLoad = firstLoad }
    },
    getters: {
        HasUrlParams: (state) => state.HasUrlParams,
        Keyword: (state) => state.Keyword,
        KeywordCategory: (state) => state.KeywordCategory,
        Specialties: (state) => state.Specialties,
        ProviderName: (state) => state.ProviderName,
        Location: (state) => state.Location,
        Distance: (state) => state.Distance,
        Insurances: (state) => state.Insurances,
        showInsurancesBlock: (state) => state.showInsurancesBlock,
        showTravelTime: (state) => state.showTravelTime,
        AcceptingNewPatients: (state) => state.AcceptingNewPatients,
        OffersOnlineScheduling: (state) => state.OffersOnlineScheduling,
        OffersVirtualVisits: (state) => state.OffersVirtualVisits,
        NextAvailability: (state) => state.NextAvailability,
        PreferredDays: (state) => state.PreferredDays,
        PreferredTimes: (state) => state.PreferredTimes,
        Genders: (state) => state.Genders,
        Languages: (state) => state.Languages,
        Latitude: (state) => state.Latitude,
        Longitude: (state) => state.Longitude,
        LocationName: (state) => state.LocationName,
        GlobalLocation: (state) => state.GlobalLocation,
        Page: (state) => state.Page,
        PageSize: (state) => state.PageSize,
        Sort: (state) => state.Sort,
        Seed: (state) => state.Seed,
        fetchingAppointments: (state) => state.fetchingAppointments,
        Appointments: (state) => state.Appointments,
        ProvidersByLocation: (state) => state.ProvidersByLocation,
        ClusterLabel: (state) => state.ClusterLabel,
        ActiveFilters: (state) => state.ActiveFilters,
        resultsLoading: (state) => state.resultsLoading,
        locationsLoading: (state) => state.locationsLoading,
        results: (state) => state.results,
        resultsLabel: (state) => state.resultsLabel,
        filterRemoved: (state) => state.filterRemoved,
        distanceRemoved: (state) => state.distanceRemoved,
        keywordRemoved: (state) => state.keywordRemoved,
        clearAll: (state) => state.clearAll,
        mobileFilterActive: (state) => state.mobileFilterActive,
        firstLoad: (state) => state.firstLoad,
        getFilterValue: (state) => (key) => {
            const value = state.ActiveFilters[key];
            if (Array.isArray(value)) {
              return value.map((item) =>
                typeof item === 'boolean'
                  ? item.toString() === 'true'
                    ? addSpacesBeforeCapitals(key)
                    : 'Not ' + addSpacesBeforeCapitals(key)
                  : item
              );
            } else {
              return typeof value === 'boolean'
                ? value.toString() === 'true'
                  ? addSpacesBeforeCapitals(key)
                  : 'Not ' + addSpacesBeforeCapitals(key)
                : value;
            }
        }
    },
    actions: {
        async fetchResults({ state, commit, rootState, dispatch }, payload) {
            if (state.resultsLoading && !state.firstLoad) {
                return;
            }

            if(!rootState.banner_only){
                commit('resultsLoading', true);

                const currentPage = payload && payload.pageOnly ? state.Page : 1;
                commit("Page", currentPage);

                const urlParams = new URLSearchParams(window.location.search);
                if(urlParams.get('zip') && !state.Latitude && !state.Longitude){
                    commit('resultsLoading', false);
                    return;
                }

                if(!state.Location && (state.Latitude && state.Longitude)){
                    commit("Location", `${state.Latitude},${state.Longitude}`);
                }

                const data = {
                    Keyword: state.Keyword ? state.Keyword : null,
                    SpecialtiesFilter: state.Specialties ? state.Specialties : null,
                    ProviderName: state.ProviderName ? state.ProviderName : null,
                    
                    conditions_symptoms: null,
                    location_name: null,
                    primary_care: null,
                    treatments_procedures: null,

                    Distance: state.Latitude ? (state.Distance || DEFAULT_DISTANCE) : null,
                    Insurance: state.Insurances,
                    AcceptingNewPatients: state.AcceptingNewPatients,
                    OffersOnlineScheduling: state.OffersOnlineScheduling,
                    OffersVirtualVisits: state.OffersVirtualVisits,
                    NextAvailability: state.NextAvailability,
                    PreferredDays: state.PreferredDays,
                    PreferredTimes: state.PreferredTimes,
                    Genders: state.Genders,
                    Languages: state.Languages,
                    Latitude: state.Latitude,
                    Location: state.Location ? state.Location : null,
                    Longitude: state.Longitude,
                    Page: currentPage,
                    PageSize: state.PageSize,
                    Sort: state.Sort,
                    Seed: state.Seed,
                };

                if (state.KeywordCategory) {
                    if (state.KeywordCategory === 'specialties') {
                        data.SpecialtiesFilter = state.Keyword || data.SpecialtiesFilter;
                    } else if (state.KeywordCategory === 'provider_name') {
                        data.ProviderName = state.Keyword || data.ProviderName;
                    } else {
                        data[state.KeywordCategory] = state.Keyword;
                    }
                }

                if (data.SpecialtiesFilter) {
                    data.Keyword = null;
                }

                if (!state.firstLoad) {
                    const urlData = { ...data };
                    
                    if (urlData.SpecialtiesFilter) {
                        urlData.specialties = urlData.SpecialtiesFilter;
                    } else {
                        urlData.specialties = null;
                    }

                    // Explicitly nullify legacy alias names so setUrlParams actively deletes them
                    urlData.SpecialtiesFilter = null;
                    urlData.specialty = null;
                    urlData.LocationName = state.LocationName;

                    dispatch('setUrlParams', urlData);
                }

                Object.keys(data).forEach(key => {
                    if (data[key] === null || data[key] === undefined || (Array.isArray(data[key]) && data[key].length === 0)) {
                        delete data[key];
                    }
                });

                const url = new URL(`${rootState.api_url}/api/Kyruus/Search`);
                const requestOpts = {
                    method: "POST", 
                    headers: { 
                        'content-type': 'application/json; charset=utf-8'
                    },
                    body: JSON.stringify(data)
                }

                try {
                    const response = await fetch(url, requestOpts).then(response => response.json());
                    
                    commit('results', response);
                    commit('resultsLoading', false);
                    dispatch('fetchAppointments');

                    if (state.firstLoad) {
                        commit('firstLoad', false);
                    }
                } catch (error) {
                    console.error('Error fetching search results:', error);
                    commit('resultsLoading', false);
                }
            }
        },
        async fetchAppointments({ state, commit, rootState }) {
            commit('fetchingAppointments', true);       
            const providers = (state.results && state.results.providers) ? state.results.providers : [];
            let epicIds = providers
                .filter(x => x.epic_id && x.epic_id.trim())
                .map(x => x.epic_id);
            if(!epicIds || epicIds.length == 0){ 
                commit('Appointments', []);
                commit('fetchingAppointments', false);
                return; 
            }
            const url = new URL(`${rootState.api_url}/api/doctorlocations/getprovidersappointmentdataenhanced?ids=${epicIds.join("|")}`);
            const requestOpts = {
                method: "GET", 
                headers: { 
                    'content-type': 'application/json; charset=utf-8'
                }
            }
            const response = await fetch(url, requestOpts)
                .then(response => response.json());

            commit('Appointments', response);
            setTimeout(() => {
                commit('fetchingAppointments', false);
            }, 500);
        },
        async fetchProvidersbyLocations({ commit, rootState }, locations) {
            commit('locationsLoading', true);
            const url = new URL(`${rootState.api_url}/api/kyruus/locations?ids=${locations}`);
            const requestOpts = {
                method: "GET", 
                headers: { 
                    'content-type': 'application/json; charset=utf-8'
                }
            }
            const response = await fetch(url, requestOpts)
                .then(response => response.json());

            commit('ProvidersByLocation', response);
            commit('locationsLoading', false);
        },
        async fetchClusterCount({ rootState },locations){
            const url = new URL(`${rootState.api_url}/api/kyruus/locations?ids=${locations}`);
            const requestOpts = {
                method: "GET", 
                headers: { 
                    'content-type': 'application/json; charset=utf-8'
                }
            }
            try {
                const response = await fetch(url, requestOpts);
            
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
            
                const data = await response.json();
                return data; // Directly return the data
            } catch (error) {
                console.error('Error during fetch:', error);
                throw error; // Propagate the error if needed
            }
        },
        removeArrayElement({ commit }, { key, index }) {
            commit('removeArrayElement', { key, index });
        },
        getParamsFromUrl({ state, commit, rootState, dispatch }) {
            return new Promise((resolve, reject) => {
                if (rootState.banner_only) {
                    resolve(true);
                } else {
                    const urlParams = new URLSearchParams(window.location.search);
                    let keywordCategory = '';
                    let keyword = '';

                    if (urlParams.get('Sort')) {
                        // Old links still carry retired sorts; fall back and rewrite the
                        // param so a dead sort is not re-shared.
                        const sort = urlParams.get('Sort');
                        if (isSupportedSort(sort)) {
                            commit("Sort", sort);
                        } else {
                            commit("Sort", DEFAULT_SORT);
                            urlParams.set('Sort', DEFAULT_SORT);
                            history.replaceState(null, null, `${window.location.pathname}?${urlParams}`);
                        }
                    }

                    if(urlParams.get('conditions_symptoms')) { keywordCategory = 'conditions_symptoms'; keyword = urlParams.get('conditions_symptoms'); }
                    if(urlParams.get('location_name')) { keywordCategory = 'location_name'; keyword = urlParams.get('location_name'); }
                    if(urlParams.get('provider_name') || urlParams.get('ProviderName')) { 
                        keywordCategory = 'provider_name'; 
                        keyword = urlParams.get('provider_name') || urlParams.get('ProviderName');
                        commit("ProviderName", keyword);
                    }
                    if (urlParams.get('primary_care')) { keywordCategory = 'primary_care'; keyword = urlParams.get('primary_care'); }
                    
                    const specialtyVal = urlParams.get('specialties') || urlParams.get('specialty') || urlParams.get('SpecialtiesFilter');
                    if (specialtyVal) {
                        keywordCategory = 'specialties';
                        keyword = specialtyVal;
                    }

                    if (keywordCategory == 'specialties' && keyword && keyword.toLowerCase() == 'primary care') {
                        keywordCategory = 'primary_care';
                        keyword = 'Primary Care Providers';
                    }

                    if (urlParams.get('treatments_procedures')) { keywordCategory = 'treatments_procedures'; keyword = urlParams.get('treatments_procedures'); }

                    // Commit after the remaps: "Primary Care" arrives as ?specialty= but is
                    // really the primary_care facet, and sending both double-filters.
                    if (keywordCategory == 'specialties') {
                        commit("Specialties", keyword);
                    }

                    if (keywordCategory == '' && urlParams.get('Keyword')) { 
                        keyword = urlParams.get('Keyword'); 
                    }

                    if (keyword) {
                        commit("Keyword", keyword);
                        if (keywordCategory) {
                            commit("KeywordCategory", keywordCategory);
                        }
                    }

                    if (urlParams.get('Location')) {
                        let location = urlParams.get('Location');
                        commit("Location", location);
                        commit("Latitude", location.split(',')[0]);
                        commit("Longitude", location.split(',')[1]);

                        const locationName = urlParams.get('LocationName');
                        if (locationName) {
                            commit("LocationName", locationName);
                            commit("addOrUpdateFilter", { key: 'LocationName', value: locationName });
                        }
                    }

                    if(urlParams.get('Distance')){ commit("Distance", parseInt(urlParams.get('Distance'))); }
                    if(urlParams.get('showTravelTime')){ commit("showTravelTime", urlParams.get('showTravelTime') === 'true'); }
                    if(urlParams.get('AcceptingNewPatients')){ commit("AcceptingNewPatients", urlParams.get('AcceptingNewPatients') === 'true'); }
                    if(urlParams.get('OffersOnlineScheduling')){ commit("OffersOnlineScheduling", urlParams.get('OffersOnlineScheduling') === 'true'); }
                    if(urlParams.get('OffersVirtualVisits')){ commit("OffersVirtualVisits", urlParams.get('OffersVirtualVisits') === 'true'); }
                    if(urlParams.get('Genders')){ commit("Genders", urlParams.get('Genders').split(',')); }
                    if(urlParams.get('Languages')){ commit("Languages", urlParams.get('Languages').split(',')); }

                    commit("HasUrlParams", true);
                    resolve(true);
                }
            });
        },
        setUrlParams({ state }, data) {
            return new Promise((resolve) => {
                    const urlParams = new URLSearchParams(window.location.search);
                    const ignoreKeys = ['Seed'];
                    for (const key in data) {
                        if(ignoreKeys.indexOf(key) === -1){
                            let value = data[key];
                            if(value && value != null && value.toString().length > 0) {
                                urlParams.set(key, value);
                            } else{
                            
                            urlParams.delete(key);
                        }
                    }
                }

                const newUrl = window.location.pathname + '?' + urlParams.toString();
                history.replaceState(null, null, newUrl);
                resolve(true);
            });
        },
        // The global "Set My Location" preference is owned by the site header
        // (Feature/Navigation preferred-location.js). This app only ever reads it -
        // an inline search here is a temporary override and must not overwrite it.
        loadGlobalLocation({ commit }){
            const cookieValue = VueCookies.get('christus-preferred-location');
            const globalLocation = cookieValue && cookieValue.location
                ? { name: cookieValue.name, lat: cookieValue.location.lat, lng: cookieValue.location.lng }
                : null;

            commit("GlobalLocation", globalLocation);
            return globalLocation;
        },
        // Searching a location narrows the results; it is not a request to re-sort
        // them. Whatever the user picked in the Sort dropdown stays picked.
        applySearchLocation({ commit }, { name, lat, lng }){
            commit("LocationName", name);
            commit("Latitude", lat);
            commit("Longitude", lng);
            commit("Location", `${lat},${lng}`);
        },
        // Clears location filtering outright. Never touches the global preference,
        // so the header keeps whatever the user set there.
        // Kept synchronous: callers clear state and then immediately fetchResults.
        clearSearchLocation({ commit }){
            commit("LocationName", null);
            commit("Latitude", null);
            commit("Longitude", null);
            commit("Location", null);
            commit("showTravelTime", null);
            commit("removeFilter", "LocationName");
            commit("removeFilter", "Distance");
        },
        // Seed the search from the global location on boot. An explicit Location in
        // the URL wins, so shared/deep links keep working. Seeding happens only here
        // and on an explicit header change - never as the result of a clear, or the
        // clear would undo itself.
        initSearchLocation({ dispatch, state, commit }){
            dispatch('loadGlobalLocation');

            const globalLocation = state.GlobalLocation;
            if (!globalLocation || state.Location) return globalLocation;

            dispatch('applySearchLocation', globalLocation);
            commit("addOrUpdateFilter", { key: 'LocationName', value: globalLocation.name });
            return globalLocation;
        },
    }
}